import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CardFor } from './cards';
import { FILL_VIEWERS } from './viewers/ViewerRegistry';
import { liveEnded, setAssistantStatus, setWaiting } from './store/chatSlice';
import { sendAction, sendLiveEnd } from './store/Actions';

// Live events in the output panel (ADR 2026-11-03-live-events-and-links). Every running one stays
// mounted, since a game or a sim keeps its state only in its card; the active one is shown.

// A live card that sits unseen and untouched this long has ended: its natural end when nothing
// else ends it.
export const IDLE_END_MS = 30 * 60 * 1000;
const IDLE_CHECK_MS = 60 * 1000;

// Only the agent's own link rows: "/live/<id> <title>", not a /live/ path inside prose.
const LIVE_URL_RE = /^\/live\/([A-Za-z0-9_-]+)(?=\s|$)/;

// The live event a chat message links to: { id, url, title }, or null.
export function liveLinkIn(text) {
  if (typeof text !== 'string') return null;
  const m = LIVE_URL_RE.exec(text);
  return m ? { id: m[1], url: m[0], title: text.slice(m.index + m[0].length).trim() } : null;
}

export function runningLive(items) {
  return (items || []).filter((i) => !i.ended);
}

// What the panel holds: the running events, and an ended one while it is still the one shown.
export function shownLive(items, active) {
  return (items || []).filter((i) => !i.ended || (i.id === active && i.card));
}

// `open` is false while the drawer is closed: the shown card is out of sight too, so it pauses.
export function LivePanel({ open = true }) {
  const dispatch = useDispatch();
  const { live, cardPush, user, station, threadId } = useSelector((s) => s.chat);
  // As GameViewer's: an action a card emits is a chat turn. It does not mark the panel stale,
  // so the live card stays where it is.
  const onAction = React.useCallback((action, structured) => {
    if (!station || !station.station) return;
    dispatch(setWaiting(true));
    dispatch(sendAction(action, station.station.station_id, station.sessionId, threadId));
    dispatch(setAssistantStatus(action.confirmation
      || (structured && structured.summary ? `${action.label || 'Action'}: ${structured.summary}`
        : (action.label || 'Action'))));
  }, [dispatch, station, threadId]);
  const running = runningLive(live.items);
  const shownItems = shownLive(live.items, live.active);
  // Ended here (the card said so, or it sat hidden too long): the server forgets it too, so a
  // reload doesn't bring it back. A tab Chrome froze fires its timers before its socket
  // reconnects, so each end is sent again on every new connection; the server ignores repeats.
  const connection = useSelector((s) => s.chat.connectionStatus);
  const connections = React.useRef(0);
  const ends = React.useRef({});   // event id → the connection it was last sent on
  const [endCount, setEndCount] = React.useState(0);
  const endHere = React.useCallback((id) => {
    dispatch(liveEnded(id));
    ends.current[id] = null;
    setEndCount((n) => n + 1);
  }, [dispatch]);
  React.useEffect(() => {
    if (connection === 'live') connections.current += 1;
  }, [connection]);
  React.useEffect(() => {
    if (connection !== 'live' || !station || !station.station) return;
    // Only events still ended here: one opened again since is held, and must not be ended again.
    const stillEnded = new Set(live.items.filter((i) => i.ended).map((i) => i.id));
    Object.keys(ends.current).forEach((id) => {
      if (!stillEnded.has(id)) delete ends.current[id];
      else if (ends.current[id] !== connections.current) {
        dispatch(sendLiveEnd(id, station.station.station_id, station.sessionId, threadId));
        ends.current[id] = connections.current;
      }
    });
  }, [connection, station, threadId, endCount, live.items, dispatch]);
  const touched = React.useRef({});
  const touch = React.useCallback((id) => { touched.current[id] = Date.now(); }, []);

  // Seen counts as touched: the clock for a hidden card starts when it leaves view.
  const prevActive = React.useRef(live.active);
  React.useEffect(() => {
    if (prevActive.current) touch(prevActive.current);
    if (live.active) touch(live.active);
    prevActive.current = live.active;
  }, [live.active, touch]);
  React.useEffect(() => {
    if (cardPush && cardPush.liveEvent) touch(cardPush.liveEvent);
  }, [cardPush, touch]);

  const runningIds = running.map((i) => i.id).join(',');
  React.useEffect(() => {
    if (!runningIds) return undefined;
    runningIds.split(',').forEach((id) => { if (!touched.current[id]) touch(id); });
    const timer = setInterval(() => {
      const now = Date.now();
      runningIds.split(',').forEach((id) => {
        if (id !== prevActive.current && now - (touched.current[id] || now) > IDLE_END_MS) {
          endHere(id);
        }
      });
    }, IDLE_CHECK_MS);
    return () => clearInterval(timer);
  }, [runningIds, endHere, touch]);

  if (!shownItems.length) return null;
  return shownItems.map((item) => {
    const shown = item.id === live.active;
    const card = item.card || {};
    const fill = FILL_VIEWERS.has(item.viewer);
    return (
      <div key={item.id} data-testid={`live-${item.id}`}
           style={{ display: shown ? 'block' : 'none', height: fill ? '100%' : undefined }}>
        <CardFor key={card.session != null ? `${item.id}-s${card.session}` : item.id}
                 structured={card} onAction={onAction} accountId={user && user.user_id}
                 fillHeight={fill} liveEventId={item.id} hidden={!shown || !open}
                 onEnd={() => { if (!item.ended) endHere(item.id); }}
                 onActivity={() => touch(item.id)} />
      </div>
    );
  });
}

// Switch the panel between its ordinary output and each running live event.
export function LiveTabs({ items, active, hasOutput, onSelect }) {
  const running = shownLive(items, active);
  if (!running.length) return null;
  const tab = (key, label, on) => (
    <button key={key} type="button" onClick={() => onSelect(key === '' ? null : key)}
            aria-pressed={on}
            style={{ flex: '0 1 auto', minWidth: 0, maxWidth: 180, overflow: 'hidden',
                     textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer',
                     fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                     border: `1px solid ${on ? '#0084ff' : '#e2e8f0'}`,
                     background: on ? '#e8f2ff' : '#ffffff', color: on ? '#0b5cad' : '#475569' }}>
      {label}
    </button>
  );
  return (
    <div data-testid="live-tabs"
         style={{ display: 'flex', gap: 6, padding: '6px 10px', overflowX: 'auto',
                  borderBottom: '1px solid #eef2f7', flexShrink: 0 }}>
      {hasOutput && tab('', 'Output', !active)}
      {running.map((i) => tab(i.id, i.ended ? `${i.title} · ended` : `● ${i.title}`, i.id === active))}
    </div>
  );
}

// A live event's link in the chat: opens it while it runs, says so once it has ended. In a room
// the event may be someone else's: `onFetch` asks for it. A game that runs in the browser opens as
// the viewer's own copy, which the link then opens.
export function LiveLink({ id, title: named, onOpen, onFetch }) {
  const item = useSelector((s) => s.chat.live.items.find((i) => i.id === id)
    || (onFetch && s.chat.live.items.find((i) => i.of === id && !i.ended)));
  const running = item ? !item.ended : !!onFetch;
  const title = (item && item.title) || named || 'Live card';
  if (!running) {
    return (
      <span data-testid="live-link" style={{ display: 'inline-block', marginTop: 4,
                                             fontSize: '0.85rem', color: '#94a3b8' }}>
        {title} · ended
      </span>
    );
  }
  return (
    <a href={`/live/${id}`} data-testid="live-link"
       onClick={(e) => { e.preventDefault(); if (item) onOpen(item.id); else onFetch(id); }}
       style={{ display: 'inline-block', marginTop: 4, fontSize: '0.85rem', fontWeight: 600,
                color: '#16a34a', textDecoration: 'none' }}>
      ● {title} · Open live ›
    </a>
  );
}
